// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import { hasLengthAtLeast } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { flattenVector, isInsignificant } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';

/**
 * Given a vector in NDC space, shift it by a 2D offset and clamp the result to the NDC frustum margin.
 * @param vec the vector in NDC space to clamp. Z is ignored.
 * @param ndcOffset a 2D vector representing the offset in NDC space.
 */
const shiftNDCVector = (vec: THREE.Vector3, ndcOffset: THREE.Vector2): THREE.Vector3 =>
  new THREE.Vector3(
    THREE.MathUtils.clamp(vec.x + ndcOffset.x, -labelTheming.maxDistance, labelTheming.maxDistance),
    THREE.MathUtils.clamp(vec.y + ndcOffset.y, -labelTheming.maxDistance, labelTheming.maxDistance),
    vec.z,
  );

/**
 * Given a list of anchor points in NDC space, returns a 2D direction vector from the first to the last.
 * @param anchorNDCs The anchor points, in NDC space.
 */
const getAnchorLayoutDirection = (anchorNDCs: THREE.Vector3[]) => {
  if (!hasLengthAtLeast(anchorNDCs, 2)) return null;
  const firstAnchorNDC = anchorNDCs[0];
  // The null coalescing here should never happen, but TypeScript doesn't know that.
  const lastAnchorNDC = anchorNDCs[anchorNDCs.length - 1] ?? anchorNDCs[1];
  const direction = flattenVector(lastAnchorNDC.clone().sub(firstAnchorNDC));
  if (isInsignificant(direction.length())) direction.set(1, 0);
  direction.normalize();
  return direction;
};

/**
 * Get the direction to push the billboard's NDC position, as a unit vector.
 *
 * If there's only one anchor point, it's the direction from the bounding box center to that anchor.
 * If there are two or more anchor points, it's the direction perpendicular to the line from the first anchor to the last,
 * so that the billboard stays centered over the measurement instead of sliding along it.
 *
 * @param anchorNDCs The anchor points, in NDC space.
 * @param centroidNDC The centroid of the anchor points in NDC space.
 * @param bboxCenterNDC The center of the bounding box in NDC space.
 */
const getPushDirection = (anchorNDCs: THREE.Vector3[], centroidNDC: THREE.Vector2, bboxCenterNDC: THREE.Vector2) => {
  const awayFromBbox = centroidNDC.clone().sub(bboxCenterNDC);
  if (hasLengthAtLeast(anchorNDCs, 2)) {
    const measurementAxisDir = getAnchorLayoutDirection(anchorNDCs);
    if (isNil(measurementAxisDir)) return null;
    const perpendicular = new THREE.Vector2(-measurementAxisDir.y, measurementAxisDir.x);
    // A line has two perpendiculars, and which one (-y, x) lands on depends on the order the
    // anchors happen to arrive in — so resolve the sign against the geometry instead, picking
    // the side facing away from the bounding box center to match what 'outward' means for a
    // single anchor. Without this the label can be pushed straight into the geometry, and
    // reversing the anchors would flip it to the other side.
    if (isInsignificant(awayFromBbox.length())) {
      // The anchors straddle the bounding box center, so neither side is the outward one.
      // Fall back to a fixed screen-space rule (+X, then +Y) to keep the choice stable —
      // the sign is pure noise here and would otherwise flip the label between frames.
      if (perpendicular.x < 0 || (isInsignificant(perpendicular.x) && perpendicular.y < 0)) perpendicular.negate();
    } else if (perpendicular.dot(awayFromBbox) < 0) {
      perpendicular.negate();
    }
    return perpendicular;
  } else if (hasLengthAtLeast(anchorNDCs, 1)) {
    if (isInsignificant(awayFromBbox.length())) awayFromBbox.set(1, 1);
    return awayFromBbox.normalize();
  }
  return null;
};

/**
 * Distance along one axis from `point` to the bounding box face that `direction` points at.
 *
 * `direction` is one component of a **unit** vector, so the result is a distance along that whole
 * vector and is comparable with the other axis'. A direction with no component on this axis never
 * crosses either of its faces, hence `Infinity`.
 */
const distanceToFace = (point: number, min: number, max: number, direction: number) => {
  if (isInsignificant(direction)) return Infinity;
  return direction > 0 ? (max - point) / direction : (min - point) / direction;
};

/**
 * Computes the initial NDC position for a label's text billboard — Phase 1 of the per-frame update.
 *
 * The billboard starts at the centroid of the anchors and is pushed clear of the geometry along a
 * direction chosen by {@link getPushDirection}, far enough for the centroid and every anchor to
 * leave the geometry's projected bounds, plus `ndcLabelOffset` of standoff. The result is clamped
 * to {@link labelTheming.maxDistance} so it stays inside the frustum.
 *
 * Returns `null` when there are no anchors to place against.
 */
export const computeInitialBillboardPlacement = (
  /** The anchor points to place the billboard against, in NDC space. */
  anchorNDCs: THREE.Vector3[],
  /** The bounding box of the geometry to avoid in NDC space. */
  bboxNDC: THREE.Box2,
  /** Whether to push the billboard outward or inward relative to the geometry. */
  directionPreference: 'outward' | 'inward',
  /** NDC standoff added on top of whatever clearance the geometry requires. */
  ndcLabelOffset: number,
  /**
   * When true, skip the geometry clearance entirely — the billboard is offset from the anchor
   * centroid by `ndcLabelOffset` alone and may sit on top of the geometry.
   */
  canPlaceInsideGeometry: boolean,
): THREE.Vector3 | null => {
  // Step 1: Find the centroid of the anchor points. Z rides along so the billboard lands at the
  // depth of what it labels — `ViewportScaledGroup` sizes it by distance to a perspective camera.
  const centroid = anchorNDCs
    .reduce((sum, anchorNDC) => sum.add(anchorNDC), new THREE.Vector3())
    .divideScalar(anchorNDCs.length);

  // Step 2: Create the push direction vector.
  // For single-anchor layouts, this is the direction from the bounding box center to the anchor.
  // For multi-anchor layouts, it's a 2D vector orthogonal to the direction from the first anchor to the last.
  const bboxCenterNDC = bboxNDC.getCenter(new THREE.Vector2());
  const pushDirection = getPushDirection(anchorNDCs, flattenVector(centroid), bboxCenterNDC);
  if (isNil(pushDirection)) return null;
  // If we're pushing inward, the push direction needs to be flipped.
  if (directionPreference === 'inward') pushDirection.negate();

  // Step 3 (skipped when canPlaceInsideGeometry is true): Find the push amount. Initially assume
  // it's zero. Everything this one vector moves has to clear the geometry — the billboard rides the
  // centroid, and Phase 3 lands each leader tip on `anchor + push` — and neither the anchors nor the
  // centroid subsumes the other, so take the largest requirement across all of them. For each point:
  const pushAmount = canPlaceInsideGeometry
    ? 0
    : [...anchorNDCs, centroid].reduce((maxPush, pointNDC) => {
        // If the point is outside the bounding box, continue.
        const flatPoint = flattenVector(pointNDC);
        if (!bboxNDC.containsPoint(flatPoint)) return maxPush;
        // Find the distance the pushDirection vector would need to be in order to push this point out
        // of the bounding box. Per axis that's the distance to the face pushDirection points at, and
        // the nearer of the two wins — clearing either axis' pair of faces clears the box.
        const distanceToEdge = Math.min(
          distanceToFace(flatPoint.x, bboxNDC.min.x, bboxNDC.max.x, pushDirection.x),
          distanceToFace(flatPoint.y, bboxNDC.min.y, bboxNDC.max.y, pushDirection.y),
        );
        // If that distance is greater than the previous max, this is the new push amount.
        return Math.max(maxPush, distanceToEdge);
      }, 0);

  // Step 4: Lengthen the push vector to the push amount plus `ndcLabelOffset` NDC units
  const totalPush = pushDirection.multiplyScalar(pushAmount + ndcLabelOffset);
  // Step 5: Get the text NDC, which is the centroid of the anchor points pushed by the push vector.
  return shiftNDCVector(centroid, totalPush);
};

/**
 * Computes leader-tip NDC positions using the billboard's **final** NDC position
 * (after clamping and overlap resolution).
 *
 * Each tip is the perpendicular foot of its anchor onto the line that passes through
 * `billboardNDC` and runs parallel to the measurement axis. This guarantees:
 *  1. The connector line (first tip to final tip) runs through `billboardNDC` — its midpoint equals
 *     `billboardNDC` for symmetric two-anchor layouts (the common case).
 *  2. The connector is parallel to the measurement axis.
 *  3. Every leader line (anchor to tip) is perpendicular to the connector.
 *
 * Call this in Phase 3 (`connectLeaderLines`) instead of re-running
 * {@link computeInitialBillboardPlacement}, so the connector follows the billboard even after
 * Phase 2 has repositioned it.
 */
export const computeMultiAnchorLeaderTipsFromBillboard = (
  /** The anchor points the leader lines run from, in NDC space. */
  anchorNDCs: THREE.Vector3[],
  billboardNDC: THREE.Vector3,
): THREE.Vector3[] | null => {
  const measurementAxisDir = getAnchorLayoutDirection(anchorNDCs);
  if (isNil(measurementAxisDir)) return null;
  // Project each anchor perpendicularly onto the line through billboardNDC ∥ measurementAxisDir.
  // dot product gives the signed distance along the axis from the billboard projection;
  // adding it back to billboard gives the foot of the perpendicular from that anchor.
  return anchorNDCs.map((anchorNDC) => {
    const signedDistance = flattenVector(anchorNDC).sub(flattenVector(billboardNDC)).dot(measurementAxisDir);
    return new THREE.Vector3(
      billboardNDC.x + signedDistance * measurementAxisDir.x,
      billboardNDC.y + signedDistance * measurementAxisDir.y,
      billboardNDC.z,
    );
  });
};
